import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react'

import produce from 'immer'
import { Worksheet } from 'src/legacy_modules/entities/worksheet/entity'
import { assertNever } from 'src/utils/assertNever'

type State = Worksheet

type Action =
  | { type: 'init'; worksheet: Worksheet }
  | {
      type: 'updateSmallItemAnswer'
      smallItemAnswerUid: string
      value: string
    }
  | {
      type: 'updateChoiceAnswer'
      smallItemAnswerUid: string
      selectedChoiceAnswerUids: string[]
    }

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'init':
      return action.worksheet
    case 'updateSmallItemAnswer':
      return produce(state, draft => {
        const mia = draft.mediumItemAnswers.find(m =>
          Object.keys(m.smallItemAnswerMap).includes(action.smallItemAnswerUid),
        )
        if (!mia) return

        const sia = mia.smallItemAnswerMap[action.smallItemAnswerUid]
        if (!sia) return

        sia.value = action.value
      })
    case 'updateChoiceAnswer':
      return produce(state, draft => {
        const mia = draft.mediumItemAnswers.find(m =>
          Object.keys(m.smallItemAnswerMap).includes(action.smallItemAnswerUid),
        )
        if (!mia) return

        const sia = mia.smallItemAnswerMap[action.smallItemAnswerUid]
        if (!sia) return

        sia.choiceAnswers.forEach(ca => {
          ca.checked = action.selectedChoiceAnswerUids.includes(ca.uid)
        })
      })
    default: {
      return assertNever(action)
    }
  }
}

type ContextValue = {
  worksheet: Worksheet
  updateSmallItemAnswer: (args: {
    smallItemAnswerUid: string
    value: string
  }) => void
  updateChoiceAnswer: (args: {
    smallItemAnswerUid: string
    selectedChoiceAnswerUids: string[]
  }) => void
}

const PreviewWorksheetContext = createContext<ContextValue | null>(null)

type ProviderProps = {
  originalWorksheet: Worksheet
  children: React.ReactNode
}

export const PreviewWorksheetProvider: React.FC<ProviderProps> = ({
  originalWorksheet,
  children,
}) => {
  const [worksheet, dispatch] = useReducer(reducer, originalWorksheet)

  // originalWorksheet が（テンプレ編集等で）差し替わった場合に state を同期する。
  // 初回マウント時は初期状態と action.worksheet が同一参照となり再レンダーは発生しない。
  useEffect(() => {
    dispatch({ type: 'init', worksheet: originalWorksheet })
  }, [originalWorksheet])

  const updateSmallItemAnswer = useCallback(
    (args: { smallItemAnswerUid: string; value: string }) =>
      dispatch({ type: 'updateSmallItemAnswer', ...args }),
    [],
  )
  const updateChoiceAnswer = useCallback(
    (args: {
      smallItemAnswerUid: string
      selectedChoiceAnswerUids: string[]
    }) => dispatch({ type: 'updateChoiceAnswer', ...args }),
    [],
  )

  const value = useMemo(
    () => ({ worksheet, updateSmallItemAnswer, updateChoiceAnswer }),
    [worksheet, updateSmallItemAnswer, updateChoiceAnswer],
  )

  return (
    <PreviewWorksheetContext.Provider value={value}>
      {children}
    </PreviewWorksheetContext.Provider>
  )
}

export const usePreviewWorksheet = () => {
  const ctx = useContext(PreviewWorksheetContext)
  if (!ctx) {
    throw new Error(
      'usePreviewWorksheet must be used within PreviewWorksheetProvider',
    )
  }
  return ctx
}
